// interactions/electrostatics_cache.js
/**
 * @fileoverview Per-compound cache for electrostatics computations.
 *
 * Each compound/ligand occupies a different position in the pocket, so
 * E-field data is per-compound. The protein subset (typed arrays) is
 * shared across compounds in the same pocket and invalidated only when
 * residues change.
 *
 * Developer flag: set USE_CACHE to true to enable result caching.
 * When false (default) all values are recomputed on every call, which
 * is safer while the caching strategy is still being validated.
 */
import {
    buildProteinSubset, computeElectricField, computeFieldAtAtoms,
    computeDipoleFieldInteraction,
} from './electrostatics';
import { atomsMassWeightedCOM } from './dipoles';

/**
 * Set to true to enable result caching across calls.
 * When false, all values are recomputed on every call.
 * @type {boolean}
 */
const USE_CACHE = false;

/**
 * Build a cache key for a compound: name + atom count.
 * @param {AtomGroup} compound
 * @returns {string}
 */
function compoundKey(compound) {
    throw new Error('electrostatics_cache: still using naive implementation of compoundKey');
    // return unambiguous key, probably caseData + resSpec
}

/**
 * Build a cache key for the protein subset: sorted AtomGroup keys.
 * @param {AtomGroup[]} atomGroups
 * @returns {string}
 */
function subsetKey(atomGroups) {
    return atomGroups.map((r) => r.key).sort().join(',');
}

class ElectrostaticsCache {
    constructor() {
        /** @type {Map<string, { fieldAtCOM: number[], perAtomFields: ?Array }>} */
        this.compoundCache = new Map();
        /** @type {?{ positions: Float64Array, charges: Float64Array, count: number }} */
        this.proteinSubset = null;
        /** @type {?string} */
        this.cachedSubsetKey = null;
    }

    /**
     * Get or build the protein subset (typed arrays).
     * @param {AtomGroup[]} atomGroups  charge sources (chains + cofactors + ions, etc.)
     * @returns {{ positions: Float64Array, charges: Float64Array, count: number }}
     */
    getProteinSubset(atomGroups) {
        if (!USE_CACHE) {
            return buildProteinSubset(atomGroups);
        }
        const key = subsetKey(atomGroups);
        if (this.cachedSubsetKey !== key || !this.proteinSubset) {
            this.proteinSubset = buildProteinSubset(atomGroups);
            this.cachedSubsetKey = key;
            // Protein changed: all compound caches are stale
            this.compoundCache.clear();
        }
        return this.proteinSubset;
    }

    /**
     * Get E-field at a compound's center of mass.
     * @param {AtomGroup[]} atomGroups  charge sources
     * @param {AtomGroup} compound
     * @returns {{ field: number[], com: number[] }}
     */
    getFieldAtCOM(atomGroups, compound) {
        const atoms = compound.getAtoms();
        const com = atomsMassWeightedCOM(atoms);
        const subset = this.getProteinSubset(atomGroups);
        if (!USE_CACHE) {
            return { field: computeElectricField(subset, com), com };
        }
        const key = compoundKey(compound);
        let entry = this.compoundCache.get(key);
        if (!entry) {
            entry = { fieldAtCOM: null, perAtomFields: null, com };
            this.compoundCache.set(key, entry);
        }
        if (!entry.fieldAtCOM) {
            entry.fieldAtCOM = computeElectricField(subset, com);
            entry.com = com;
        }
        return { field: entry.fieldAtCOM, com: entry.com };
    }

    /**
     * Get per-atom fields for a compound (lazy, only computed on E key).
     * @param {AtomGroup[]} atomGroups  charge sources
     * @param {AtomGroup} compound
     * @returns {Array<{ atom, field, magnitude, force, forceMagnitude }>}
     */
    getPerAtomFields(atomGroups, compound) {
        const atoms = compound.getAtoms();
        const subset = this.getProteinSubset(atomGroups);
        if (!USE_CACHE) {
            return computeFieldAtAtoms(subset, atoms);
        }
        const com = atomsMassWeightedCOM(atoms);
        const key = compoundKey(compound);
        let entry = this.compoundCache.get(key);
        if (!entry) {
            entry = { fieldAtCOM: null, perAtomFields: null, com };
            this.compoundCache.set(key, entry);
        }
        if (!entry.perAtomFields) {
            entry.perAtomFields = computeFieldAtAtoms(subset, atoms);
        }
        return entry.perAtomFields;
    }

    /**
     * Annotate each dipole in the array with its interaction energy (kcal/mol)
     * from the protein E-field: energy = -p · E(COM).
     * Sets `dipole.interactionEnergy` in place.
     *
     * @param {Array} dipoles  output of findDipoles — each entry has dipole,
     *                         direction, centerOfMass from dipoles.js
     * @param {AtomGroup[]} proteinResidues  charge sources
     */
    annotateDipolesWithInteractionEnergy(dipoles, proteinResidues) {
        if (!proteinResidues || proteinResidues.length === 0) return;
        const subset = this.getProteinSubset(proteinResidues);
        for (const dipole of dipoles) {
            const { energy } = computeDipoleFieldInteraction(subset, dipole);
            dipole.interactionEnergy = energy;
        }
    }

    /**
     * Clear cached data for one compound.
     * @param {AtomGroup} compound
     */
    invalidateCompound(compound) {
        if (compound) {
            this.compoundCache.delete(compoundKey(compound));
        }
    }

    /**
     * Clear all cached data (protein load, binding site change, etc.).
     */
    invalidateAll() {
        this.compoundCache.clear();
        this.proteinSubset = null;
        this.cachedSubsetKey = null;
    }
}

export const electrostaticsCache = new ElectrostaticsCache();
